import { useMemo } from 'react'
import { estimatedCostRows, projectValueRows, type FigurePanel, type FigureRow } from '@project/contracts'
import { Money, Panel } from '../design/index.js'
import type { LineRow } from '../app/api.js'

/** One block of figures: label on the left, amount on the right, total ruled. */
export const FigureTable = ({ rows }: { rows: readonly { label: string; amount: string; emphasis?: boolean }[] }) => (
  <table className="totals">
    <tbody>
      {rows.map((line) => (
        <tr key={line.label} className={line.emphasis ? 'is-total' : undefined}>
          <td>{line.label}</td>
          <td><Money value={line.amount} /></td>
        </tr>
      ))}
    </tbody>
  </table>
)

/** The blocks the server presents beside the amount summary (Summary tab). */
export const FigurePanels = ({ panels }: { panels: readonly FigurePanel[] }) => (
  <div className="figure-panels" style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
    {panels.map((panel) => (
      <Panel key={panel.key} title={panel.title}>
        <FigureTable rows={panel.lines} />
      </Panel>
    ))}
  </div>
)

/**
 * Estimated Cost Summary and Project Value Summary at the foot of the
 * project form, worked from the grids as they are typed (reference: Lumpsum
 * reads 2.00 the moment a second 1.00 lump sum is added). The server presents
 * the same figures from the same function once the project is saved.
 */
export const LiveProjectFigures = ({ lines, interstate }: { lines: readonly LineRow[]; interstate: boolean }) => {
  const cost = useMemo<FigureRow[]>(() => estimatedCostRows(lines), [lines])
  const value = useMemo<FigureRow[]>(() => projectValueRows(lines, interstate), [lines, interstate])
  return (
    <FigurePanels
      panels={[
        { key: 'estimatedCost', title: 'Estimated Cost Summary', lines: cost },
        { key: 'projectValue', title: 'Project Value Summary', lines: value },
      ]}
    />
  )
}
